import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Send, RotateCcw } from 'lucide-react';
import { CircularProgress } from '../components/CircularProgress';
import { useDailyHabits } from '../lib/habitState';
import { useSettings, formatTodayLong } from '../lib/settings';
import { useProjectTime, formatDuration } from '../lib/projectTime';
export function LogProgress() {
  const navigate = useNavigate();
  const settings = useSettings();
  const {
    habits,
    toggleHabit,
    completedCount,
    totalCount
  } = useDailyHabits();
  const {
    minutesLogged,
    workedOn,
    addMinutes,
    resetMinutes,
    setWorkedOn
  } = useProjectTime();
  const totalHabits = totalCount;
  const hpPercentage = totalHabits === 0 ? 0 : completedCount / totalHabits * 100;
  const goalMinutes = settings.ppHours * 60 + settings.ppMinutes;
  const ppPercentage = goalMinutes === 0 ? 0 : Math.round(minutesLogged / goalMinutes * 100);
  const availableProjects = settings.projects.filter(p => p && p.trim().length > 0);
  // Default selection to first available project if current pick is no longer valid
  useEffect(() => {
    if (availableProjects.length === 0) {
      if (workedOn !== '') setWorkedOn('');
      return;
    }
    if (!availableProjects.includes(workedOn)) {
      setWorkedOn(availableProjects[0]);
    }
  }, [availableProjects, workedOn, setWorkedOn]);
  return <div className="max-w-2xl mx-auto p-8 pb-24" data-id="element-82">
      {/* Header */}
      <div className="flex items-center justify-between mb-8" data-id="element-83">
        <button onClick={() => navigate(-1)} className="p-2 -ml-2 text-gray-600 hover:bg-gray-100 rounded-full transition-colors" data-id="element-84">
          <ArrowLeft className="w-5 h-5" data-id="element-85" />
        </button>
        <span className="text-sm font-medium text-gray-500" data-id="element-86">
          {formatTodayLong()}
        </span>
        <span className="w-16" data-id="element-87" />
      </div>

      <div className="space-y-6" data-id="element-88">
        {/* Today's Stats */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-id="element-89">
          <h2 className="text-lg font-semibold text-gray-900 mb-6 text-center" data-id="element-90">
            Today's Progress
          </h2>

          <div className="space-y-6" data-id="element-91">
            {/* HP — fills right-to-left */}
            <div className="w-full" data-id="element-92">
              <div className="flex justify-between items-end mb-2" data-id="element-93">
                <span className="text-sm font-semibold text-gray-900" data-id="element-94">
                  HP {completedCount}/{totalHabits}
                </span>
              </div>
              <div className="h-2 w-full rounded-full overflow-hidden bg-gray-100 flex justify-end" role="progressbar" aria-valuenow={completedCount} aria-valuemin={0} aria-valuemax={totalHabits} aria-label={`Habit progress: ${completedCount} of ${totalHabits} habits completed`} data-id="element-95">
                <div className="h-full rounded-full bg-emerald-500 transition-all duration-500 ease-out" style={{
                width: `${hpPercentage}%`
              }} data-id="element-96" />
              </div>
            </div>

            {/* PP — fills right-to-left */}
            <div className="w-full" data-id="element-97">
              <div className="flex justify-between items-end mb-2" data-id="element-98">
                <span className="text-sm font-semibold text-gray-900" data-id="element-99">
                  PP {formatDuration(minutesLogged)} /{' '}
                  {formatDuration(goalMinutes)}
                </span>
              </div>
              <div className="h-2 w-full rounded-full overflow-hidden bg-gray-100 flex justify-end" role="progressbar" aria-valuenow={minutesLogged} aria-valuemin={0} aria-valuemax={goalMinutes} aria-label={`Project progress: ${formatDuration(minutesLogged)} of ${formatDuration(goalMinutes)} logged`} data-id="element-100">
                <div className="h-full rounded-full bg-indigo-500 transition-all duration-500 ease-out" style={{
                width: `${Math.min(ppPercentage, 100)}%`
              }} data-id="element-101" />
              </div>
            </div>
          </div>
        </div>

        {/* Habits */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-id="element-102">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 text-center" data-id="element-103">
            Habits
          </h2>

          {habits.length === 0 ? <div className="text-sm text-gray-500 p-4 bg-gray-50 rounded-xl text-center" data-id="element-104">
              No habits configured yet.{' '}
              <button onClick={() => navigate('/settings')} className="text-indigo-600 font-medium hover:underline" data-id="element-105">
                Add some in Settings
              </button>
              .
            </div> : <div className="space-y-1" data-id="element-106">
              {habits.map(habit => <label key={habit.text} className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-xl cursor-pointer transition-colors group" data-id="element-107">
                  <input type="checkbox" checked={habit.completed} onChange={() => toggleHabit(habit.text)} className="sr-only peer" data-id="element-108" />
                  <div className={`w-5 h-5 rounded flex items-center justify-center border transition-colors shrink-0 ${habit.completed ? 'bg-indigo-500 border-indigo-500 text-white' : 'border-gray-300 group-hover:border-indigo-400'}`} data-id="element-109">
                    {habit.completed && <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-110">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" data-id="element-111" />
                      </svg>}
                  </div>
                  <span className={`text-sm transition-colors ${habit.completed ? 'text-gray-400 line-through' : 'text-gray-700'}`} data-id="element-112">
                    {habit.text}
                  </span>
                </label>)}
            </div>}
        </div>

        {/* Deep Work */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-id="element-113">
          <h2 className="text-lg font-semibold text-gray-900 mb-6 text-center" data-id="element-114">
            Projects / Deep Work
          </h2>

          <div className="flex items-center gap-6 mb-8" data-id="element-115">
            <CircularProgress percentage={ppPercentage} size={100} strokeWidth={8} colorClass={ppPercentage >= 100 ? 'text-emerald-500' : 'text-indigo-500'} data-id="element-116" />
            <div data-id="element-117">
              <div className="text-2xl font-bold text-gray-900" data-id="element-118">
                {formatDuration(minutesLogged)} logged
              </div>
              <div className="text-sm text-gray-500" data-id="element-119">
                Goal: {formatDuration(goalMinutes)}
              </div>
            </div>
          </div>

          <div className="flex gap-3 mb-6" data-id="element-120">
            {[{
            label: '+15m',
            delta: 15
          }, {
            label: '+30m',
            delta: 30
          }, {
            label: '+60m',
            delta: 60
          }].map(({
            label,
            delta
          }) => <button key={label} onClick={() => addMinutes(delta)} className="flex-1 py-2 bg-gray-400 hover:bg-gray-500 text-white text-sm font-medium rounded-full transition-colors active:scale-95" data-id="element-121">
                {label}
              </button>)}
            {minutesLogged > 0 && <button onClick={resetMinutes} aria-label="Reset logged time" className="px-3 py-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors" data-id="element-122">
                <RotateCcw className="w-4 h-4" data-id="element-123" />
              </button>}
          </div>

          <div className="bg-gray-50 rounded-xl p-4 border border-gray-100" data-id="element-124">
            {availableProjects.length === 0 ? <div className="flex items-center justify-between text-sm" data-id="element-125">
                <span className="text-gray-500" data-id="element-126">No projects configured.</span>
                <button onClick={() => navigate('/settings')} className="text-indigo-600 font-medium hover:underline" data-id="element-127">
                  Add in Settings
                </button>
              </div> : <label className="flex items-center gap-2 text-sm" data-id="element-128">
                <span className="text-gray-500 shrink-0" data-id="element-129">Worked on:</span>
                <select value={workedOn} onChange={e => setWorkedOn(e.target.value)} className="bg-transparent border-none focus:ring-0 p-0 text-gray-900 font-medium w-full cursor-pointer outline-none" data-id="element-130">
                  {availableProjects.map(project => <option key={project} value={project} data-id="element-131">
                      {project}
                    </option>)}
                </select>
              </label>}
          </div>
        </div>

        {/* Publish Button */}
        <button onClick={() => navigate('/reflection')} className="w-full py-4 bg-indigo-500 hover:bg-indigo-600 text-white font-semibold rounded-xl shadow-sm transition-colors flex items-center justify-center gap-2 mt-8" data-id="element-132">
          <Send className="w-4 h-4" data-id="element-133" />
          Publish Entry
        </button>
      </div>
    </div>;
}